/**
 * Represents a single node in the dropdown configuration tree.
 * The actual shape comes from the backend JSON (label, value, backend_key, table, children, ...).
 */
type DropdownNode = any;

/**
 * Checks whether a dropdown node represents the requested field.
 *
 * The field identifier coming from the segment build can match either:
 * - backend_key: full technical identifier (preferred and unique),
 * - value: legacy / shorter identifier (fallback for backward compatibility).
 *
 * @param item        Single dropdown node to test.
 * @param targetField Field identifier coming from the segment build (field).
 * @returns           True when the node corresponds to the requested field.
 */
const matchesFieldOnly = (item: any, targetField: string) =>
  item?.backend_key === targetField || item?.value === targetField;

/**
 * Finds detailed information for a specific field within the dropdown tree.
 *
 * It walks the tree and returns:
 * - id:   stable identifier composed from the path and the current label,
 * - path: array of labels from the root to the matching node,
 * - selectOptions: additional options for select-type fields, if present.
 *
 * The search takes into account:
 * - targetField:  field identifier (backend_key or value),
 * - targetTable:  table from the segment build, to distinguish fields sharing the same value,
 * - currentTable: effective table propagated from parent nodes, when children do not define their own.
 *
 * @param data         Root dropdown tree or subtree.
 * @param targetField  Field identifier from the segment build (field).
 * @param targetTable  Table identifier from the segment build (table).
 * @param path         Accumulated labels from the root to the current node.
 * @param currentTable Currently effective table while walking the tree.
 * @returns            Object with id, path and selectOptions, or null when nothing matches.
 *
 * @example
 * const details = findItemDetails(dataDropdown, "brand", "cde_dev_gbq_euw2_ccdpgamma_001.entity_consumer_table");
 */
const findItemDetails = (
  data: DropdownNode[],
  targetField: string,
  targetTable?: string,
  path: string[] = [],
  currentTable?: string | null
): any => {
  // Iterate over all nodes at the current level of the tree.
  for (const item of data) {
    // Resolve the effective table for this node:
    // - prefer item.table or item.table_name when present,
    // - otherwise inherit table from the parent level (currentTable).
    const nextTable = (item.table || item.table_name || currentTable) ?? null;

    // Check if the current node represents the requested field.
    const matchesField = matchesFieldOnly(item, targetField);

    // Node matches the field and either:
    // - no targetTable is specified,
    // - or there is no effective table,
    // - or the effective table exactly equals the target table.
    if (matchesField && (!targetTable || !nextTable || nextTable === targetTable)) {
      return {
        // Build a stable id from the accumulated path and the current label.
        id: path.length ? `${path.join("-")}-${item.label}` : item.label,
        // Extend the path with the current label.
        path: [...path, item.label],
        // Pass through select options if the node provides them.
        selectOptions: item.options || null,
      };
    }

    // If the node has children, continue the search recursively in the subtree.
    if (item.children) {
      const result = findItemDetails(
        item.children,
        targetField,
        targetTable,
        [...path, item.label], // extend the path with the current label
        nextTable              // propagate resolved table into the subtree
      );
      // If a match was found in the subtree, return it immediately.
      if (result) return result;
    }
  }

  // No matching node found in this branch.
  return null;
};

/**
 * Finds contextual information for a specific field within the dropdown tree.
 *
 * The function returns:
 * - listFilter:  all sibling nodes at the level where the field is defined,
 * - parentPath:  path of labels from the root to the parent of the matching node.
 *
 * This is used to populate the list of selectable filters and the display path
 * in the UI (for example: "Consumer Attributes → Consumer Profile").
 *
 * @param data         Root dropdown tree or subtree.
 * @param targetField  Field identifier from the segment build (field).
 * @param targetTable  Table identifier from the segment build (table).
 * @param path         Accumulated labels from the root to the current node.
 * @param currentTable Currently effective table while walking the tree.
 * @returns            Object with listFilter and parentPath, or null when nothing matches.
 *
 * @example
 * const context = findItemContext(dataDropdown, "brand", "cde_dev_gbq_euw2_ccdpgamma_001.entity_consumer_table");
 */
const findItemContext = (
  data: DropdownNode[],
  targetField: string,
  targetTable?: string,
  path: string[] = [],
  currentTable?: string | null
): any => {
  // Iterate over all nodes at the current level of the tree.
  for (const item of data) {
    // Resolve the effective table for this node.
    const nextTable = (item.table || item.table_name || currentTable) ?? null;

    // When a node has children, we want to inspect and potentially match
    // one of those children to the target field and table.
    if (item.children) {
      // Try to find a child that matches the requested field and table.
      const found = item.children.find((child: any) => {
        // Resolve the effective table for the child node.
        const childTable =
          (child.table || child.table_name || nextTable) ?? null;
        // Check if child represents the requested field.
        const matchesField = matchesFieldOnly(child, targetField);
        if (!matchesField) return false;
        // If there is no target table or no child table, accept the match.
        if (!targetTable || !childTable) return true;
        // Otherwise require exact table match.
        return childTable === targetTable;
      });

      // If a matching child was found, return its siblings and the path to the parent.
      if (found) {
        return {
          // All siblings at this level, used to build the filter dropdown.
          listFilter: item.children,
          // Path of labels from root to the parent of the matching child.
          parentPath: [...path, item.label],
        };
      }

      // If no child matched directly, continue searching deeper in the subtree.
      const result = findItemContext(
        item.children,
        targetField,
        targetTable,
        [...path, item.label], // extend path with current label
        nextTable              // propagate effective table
      );
      // If a match was found deeper in the tree, return it.
      if (result) return result;
    }
  }

  // No contextual information found for the requested field in this branch.
  return null;
};

/**
 * Finds a dropdown node for a specific field and optionally table.
 *
 * This function is used when we only need the node itself (for example,
 * to read its label, type, or backend_key), rather than the path or context.
 *
 * The function walks the tree and returns the first node where:
 * - the field matches (backend_key or value),
 * - and the effective table matches the requested table (if provided).
 *
 * @param data         Root dropdown tree or subtree.
 * @param targetField  Field identifier from the segment build (field).
 * @param targetTable  Table identifier from the segment build (table).
 * @param currentTable Currently effective table while walking the tree.
 * @returns            Matching dropdown node, or null when nothing matches.
 *
 * @example
 * const node = findValue(dataDropdown, "brand", "cde_dev_gbq_euw2_ccdpgamma_001.entity_consumer_table");
 */
const findValue = (
  data: DropdownNode[],
  targetField: string,
  targetTable?: string,
  currentTable?: string | null
): any => {
  // Iterate over all nodes at the current level of the tree.
  for (const item of data) {
    // Resolve the effective table for this node.
    const nextTable = (item.table || item.table_name || currentTable) ?? null;

    // Check whether the node represents the target field.
    const matchesField = matchesFieldOnly(item, targetField);

    // Accept the node when the field matches and the table constraint is satisfied.
    if (matchesField && (!targetTable || !nextTable || nextTable === targetTable)) {
      return item;
    }

    // If the node has children, continue searching in the subtree.
    if (item.children) {
      const found = findValue(item.children, targetField, targetTable, nextTable);
      if (found) return found;
    }
  }

  // No matching node found in this branch.
  return null;
};

/**
 * Converts raw segment data coming from the backend into a structure
 * suitable for the segment form (create/edit/summary views).
 *
 * It:
 * - maps include/exclude sections and conditions,
 * - resolves each condition's field against the dropdown tree (dataDropdown),
 * - enriches conditions with human-readable paths, labels and select options.
 *
 * @param country          Country identifier of the segment.
 * @param segmentName      Current segment name.
 * @param searchSegmentName Name used when searching for segments.
 * @param path             Path or folder location where the segment is stored.
 * @param exclude          Exclusion conditions structure from the backend build.
 * @param include          Inclusion conditions structure from the backend build.
 * @param dataDropdown     Dropdown configuration tree used to resolve fields.
 * @param user             User identifier or object creating/editing the segment.
 * @param brand            Brand associated with the segment.
 * @param folder           Folder or grouping information for the segment.
 * @param type             Type of operation (create, edit, check, etc.).
 * @param segmentId        Unique identifier of the segment.
 * @returns                Object with sections and exclusions, ready for the form.
 *
 * @example
 * const formData = formatValuesSegment({
 *   include: backendInclude,
 *   exclude: backendExclude,
 *   dataDropdown,
 *   country,
 *   segmentName,
 *   ...
 * });
 */
const formatValuesSegment = ({
  country = null,
  segmentName = null,
  searchSegmentName = null,
  path = null,
  exclude,
  include,
  dataDropdown,
  user,
  brand,
  folder,
  type,
  segmentId,
}: any): any => {
  /**
   * Maps an array of backend sections into the internal form representation.
   *
   * Each section:
   * - keeps the global operator from the include object,
   * - stores its own inSectionOperator (logic) used between conditions,
   * - maps each condition to a rich object with path, label, options, etc.
   *
   * @param sections Raw sections array coming from the backend include/exclude.
   * @returns        Mapped sections array for the form.
   */
  const mapSections = (sections: any[]) =>
    sections?.map(({ operator: inSectionOperator, conditions }: any) => {
      // Operator defined at the include level (global operator between sections).
      const globalOperator = include?.operator;

      return {
        // Store the global include operator at the section level.
        operator: globalOperator,
        // Map each backend condition into a rich form condition object.
        conditions: conditions?.map(
          ({ field = "", operator: conditionOperator = "", table, value = "" }: any) => {
            // Resolve the matching field node in the dropdown tree.
            const node = findValue(dataDropdown, field, table);
            // Resolve contextual information (siblings and parent path).
            const context = findItemContext(dataDropdown, field, table);
            // Resolve details: id, full path and select options.
            const details = findItemDetails(dataDropdown, field, table);

            // Safely destructure context values, providing defaults when missing.
            const { listFilter = [], parentPath } = context || {};

            // Build the condition object consumed by the form.
            const res = {
              // Unique id of the condition, built from the field path.
              id: details?.id,
              // Human-readable path of labels leading to the field.
              path: details?.path || [],
              // Label of the selected field shown in the UI (can be replaced with backend_key if needed).
              selectedValue: node?.label || "",
              // Field type (e.g. select, number, date).
              type: node?.type,
              // Field identifier used when sending payload to the backend.
              field,
              // Dropdown options for select-type fields.
              selectOptions: details?.selectOptions || [],
              // Initial value set for the condition.
              value,
              // Operator used between conditions inside this section (AND/OR).
              logic: inSectionOperator,
              // All sibling filters at this level, used for field dropdown.
              listFilter,
              // Path of parent labels above this field.
              parentPath,
              // Table identifier associated with this field.
              table,
              // Operator for this specific condition (e.g. "is exactly").
              operator: conditionOperator,
            };

            return res;
          }
        ),
      };
    });

  // Build the complete payload consumed by the segment form.
  const payload: any = {
    // Basic segment metadata.
    country,
    segmentName,
    searchSegmentName,
    path,
    brand,
    folder,
    user,
    type,
    segmentId,
    // Inclusion sections mapped from the backend include structure.
    sections: include?.sections
      ? mapSections(include?.sections)
      : [{ conditions: [], operator: "AND" }],
    // Exclusion sections mapped from the backend exclude structure.
    exclusions: exclude?.sections ? mapSections(exclude?.sections) : [],
  };

  // Return the fully formatted data ready to be loaded into the form.
  return payload;
};

export default formatValuesSegment;